import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  getDailyPaperSubjects, getDailyPaperStatus, generateDailyPaper, getDailyPaperHistory,
} from '../../api';

// Daily Test (cumulative paper).
// The teacher logs the concepts he taught; the student presses one button and gets a
// single 45-question paper that mixes today's work with older material. Untimed, and
// only one paper may be open at a time — so this page either offers "resume" or "generate",
// never both.

function ScorePill({ pct }) {
  if (pct == null) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const cls = pct >= 80 ? 'badge-green' : pct < 50 ? 'badge-red' : 'badge-yellow';
  return <span className={`badge ${cls}`}>{Math.round(pct)}%</span>;
}

function ErrorCard({ children }) {
  return (
    <div className="card" style={{ borderColor: 'var(--danger)', color: 'var(--danger)', marginBottom: 16, fontSize: 14 }}>
      {children}
    </div>
  );
}

// History rows carry subject_id, not a name.
const SUBJECT_NAMES = { 1: 'Physics', 2: 'Chemistry', 3: 'Biology' };

function HistoryTable({ rows, onOpen }) {
  if (!rows || rows.length === 0) {
    return <div className="empty-state" style={{ padding: '28px 0' }}>No daily papers finished yet.</div>;
  }
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Date</th>
          <th>Subject</th>
          <th style={{ textAlign: 'center' }}>Questions</th>
          <th style={{ textAlign: 'center' }}>Correct</th>
          <th style={{ textAlign: 'right' }}>Accuracy</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(h => (
          <tr key={h.test_id} style={{ cursor: 'pointer' }} onClick={() => onOpen(h)}>
            <td>
              {h.submitted_at ? new Date(h.submitted_at).toLocaleDateString() : (h.started_at ? new Date(h.started_at).toLocaleDateString() : '—')}
              {h.status && h.status !== 'completed' && (
                <div><span className="badge badge-yellow" style={{ marginTop: 4 }}>Not finished</span></div>
              )}
            </td>
            <td>{h.subject_name || SUBJECT_NAMES[h.subject_id] || 'Physics'}</td>
            <td style={{ textAlign: 'center' }}>{h.total_questions ?? '—'}</td>
            <td style={{ textAlign: 'center' }}>{h.correct ?? '—'}</td>
            <td style={{ textAlign: 'right' }}><ScorePill pct={h.accuracy_pct} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function DailyPaper() {
  const navigate = useNavigate();
  const location = useLocation();
  // The same component serves /student/daily-paper and /student/daily-paper/history.
  const historyOnly = location.pathname.endsWith('/history');

  const [picked, setPicked] = useState(null);      // subject_id chosen in the chooser
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');              // hard error (red card)
  const [noConcepts, setNoConcepts] = useState(''); // friendly "teacher hasn't logged" state

  const subjectsQ = useQuery({
    queryKey: ['dailyPaperSubjects'],
    queryFn: () => getDailyPaperSubjects().then(r => r.data),
    enabled: !historyOnly,
  });
  const statusQ = useQuery({
    queryKey: ['dailyPaperStatus'],
    queryFn: () => getDailyPaperStatus().then(r => r.data),
    enabled: !historyOnly,
  });
  const historyQ = useQuery({
    queryKey: ['dailyPaperHistory'],
    queryFn: () => getDailyPaperHistory(20).then(r => r.data),
  });

  const subjects = subjectsQ.data?.subjects || [];
  const live = statusQ.data?.live || null;
  const last = statusQ.data?.last_completed || null;
  const historyRows = Array.isArray(historyQ.data) ? historyQ.data : (historyQ.data?.papers || historyQ.data?.history || []);

  const openHistoryRow = (h) => navigate(`/student/daily-paper/${h.test_id}/result`);

  const generate = async () => {
    if (!picked) { setErr('Choose a subject first.'); return; }
    setBusy(true); setErr(''); setNoConcepts('');
    try {
      const r = await generateDailyPaper(picked, 45);
      const d = r.data;
      // A short paper is not a failure — carry the notice into the test page.
      navigate(`/student/daily-paper/${d.test_id}`, {
        state: d.short ? { shortNotice: `Only ${d.total_questions} questions were available today.` } : undefined,
      });
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 409) {
        // Already open. FastAPI nests the router's dict under `detail`, so the
        // test_id lives at data.detail.test_id — go straight to that paper.
        const openId = detail && typeof detail === 'object' ? detail.test_id : e.response?.data?.test_id;
        if (openId) { navigate(`/student/daily-paper/${openId}`); return; }
        setErr('You already have a paper open. Reloading this page will show the Resume button.');
        statusQ.refetch();
      } else if (status === 400) {
        setNoConcepts(typeof detail === 'string' ? detail : 'No concepts have been logged for today yet.');
      } else {
        setErr(typeof detail === 'string' ? detail : 'Could not build the paper. Check your connection and press the button again.');
      }
    } finally { setBusy(false); }
  };

  // ---------- HISTORY-ONLY VIEW ----------
  if (historyOnly) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 28 }}>🗓️ Daily Test History</h1>
            <p style={{ color: 'var(--text-muted)', marginTop: 4 }}>Every daily paper you have finished. Tap a row to open its result.</p>
          </div>
          <button className="btn btn-ghost" onClick={() => navigate('/student/daily-paper')}>Back to Daily Test</button>
        </div>
        {historyQ.isError && <ErrorCard>Could not load your history. Refresh the page to try again.</ErrorCard>}
        <div className="card">
          {historyQ.isLoading ? <div className="spinner"></div> : <HistoryTable rows={historyRows} onOpen={openHistoryRow} />}
        </div>
      </div>
    );
  }

  // ---------- HUB ----------
  const loading = subjectsQ.isLoading || statusQ.isLoading;

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 28 }}>📅 Daily Test</h1>
        <p style={{ color: 'var(--text-muted)', marginTop: 4 }}>
          One cumulative 45-question paper, built from everything your teacher has taught so far.
          There is no timer — your answers save as you go, so you can stop and come back later.
        </p>
      </div>

      {err && <ErrorCard>{err}</ErrorCard>}

      {(subjectsQ.isError || statusQ.isError) && (
        <ErrorCard>
          Could not reach the server. Check your internet connection and refresh this page.
        </ErrorCard>
      )}

      {loading ? <div className="spinner"></div> : (
        <>
          {live ? (
            // ---------- RESUME ----------
            <div className="card" style={{ marginBottom: 20, borderTop: '3px solid var(--warning)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                <div>
                  <span className="badge badge-yellow">Paper open</span>
                  <h2 style={{ fontSize: 20, marginTop: 8 }}>{live.title || 'Daily Test'}</h2>
                  <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 4 }}>
                    You have a paper open — <strong>{live.answered} of {live.total_questions} answered</strong>.
                    {live.subject_name ? ` ${live.subject_name}.` : ''}
                    {live.started_at ? ` Started ${new Date(live.started_at).toLocaleString()}.` : ''}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 6 }}>
                    Finish this one before generating another — only one paper can be open at a time.
                  </div>
                </div>
                <button className="btn btn-primary" onClick={() => navigate(`/student/daily-paper/${live.test_id}`)}>
                  Resume this paper
                </button>
              </div>
              <div style={{ marginTop: 14, height: 6, background: 'var(--border)', borderRadius: 3 }}>
                <div style={{
                  height: '100%', borderRadius: 3, background: 'var(--primary)',
                  width: `${live.total_questions ? (live.answered / live.total_questions) * 100 : 0}%`,
                }} />
              </div>
            </div>
          ) : noConcepts ? (
            // ---------- FRIENDLY EMPTY STATE (400: nothing logged) ----------
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="empty-state" style={{ padding: '30px 20px' }}>
                <div style={{ fontSize: 34, marginBottom: 10 }}>📋</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
                  No paper can be built yet
                </div>
                <div style={{ fontSize: 14, maxWidth: 460, margin: '0 auto' }}>
                  {noConcepts}
                  <br /><br />
                  Your teacher needs to log today's concepts first. Once he has, come back to this
                  page and press <strong>Generate today's paper</strong> again.
                </div>
                <button className="btn btn-ghost" style={{ marginTop: 16 }}
                  onClick={() => { setNoConcepts(''); subjectsQ.refetch(); statusQ.refetch(); }}>
                  Check again
                </button>
              </div>
            </div>
          ) : (
            // ---------- SUBJECT CHOOSER ----------
            <div className="card" style={{ marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, marginBottom: 4 }}>Choose a subject</h2>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>
                45 questions, mixing today's concepts with older ones you need to keep warm.
              </p>

              {subjects.length === 0 ? (
                <div className="empty-state" style={{ padding: '24px 0' }}>No subjects are set up yet.</div>
              ) : subjects.map(s => {
                const selected = picked === s.subject_id;
                const disabled = !s.enabled;
                return (
                  <div key={s.subject_id}
                    onClick={() => { if (!disabled) { setPicked(s.subject_id); setErr(''); } }}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px', marginBottom: 10,
                      border: `1.5px solid ${selected ? 'var(--primary)' : 'var(--border)'}`, borderRadius: 10,
                      background: disabled ? 'var(--bg)' : selected ? 'var(--primary-light)' : 'var(--surface)',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.6 : 1,
                    }}>
                    <span style={{
                      width: 18, height: 18, borderRadius: '50%', flexShrink: 0, marginTop: 3,
                      border: `2px solid ${disabled ? 'var(--border)' : selected ? 'var(--primary)' : '#CBD5E1'}`,
                      background: selected ? 'var(--primary)' : 'transparent',
                      boxShadow: selected ? 'inset 0 0 0 3px var(--surface)' : 'none',
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600 }}>{s.name}</span>
                        {disabled && <span className="badge badge-yellow">{s.note || 'Not enabled yet'}</span>}
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
                        {s.concepts_taught ?? 0} concept{s.concepts_taught === 1 ? '' : 's'} taught ·{' '}
                        {s.questions_available ?? 0} question{s.questions_available === 1 ? '' : 's'} available
                      </div>
                    </div>
                  </div>
                );
              })}

              <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 6 }}
                disabled={busy || !picked} onClick={generate}>
                {busy ? 'Building your paper…' : "Generate today's paper"}
              </button>
              {!picked && subjects.length > 0 && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', marginTop: 8 }}>
                  Pick a subject above to enable this button.
                </div>
              )}
            </div>
          )}

          {last && (
            <div className="card" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>
                Last paper{last.submitted_at ? ` on ${new Date(last.submitted_at).toLocaleDateString()}` : ''}:{' '}
                <strong style={{ color: 'var(--text)' }}>{last.correct} of {last.total_questions} correct</strong>{' '}
                <ScorePill pct={last.accuracy_pct} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/student/daily-paper/${last.test_id}/result`)}>
                  See that result
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => navigate('/student/daily-paper/history')}>
                  All past papers →
                </button>
              </div>
            </div>
          )}

          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h2 style={{ fontSize: 18 }}>Recent daily papers</h2>
              <span style={{ color: 'var(--primary)', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}
                onClick={() => navigate('/student/daily-paper/history')}>See all →</span>
            </div>
            {historyQ.isLoading ? <div className="spinner"></div>
              : historyQ.isError ? <div style={{ color: 'var(--danger)', fontSize: 13 }}>Could not load your past papers. Refresh the page to try again.</div>
                : <HistoryTable rows={historyRows.slice(0, 8)} onOpen={openHistoryRow} />}
          </div>
        </>
      )}
    </div>
  );
}
